import { IDomainWideProjectRole } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import * as React from "react";
import { ChangeEvent, useEffect, useState } from "react";
import { DomainService } from "../../../../services/domain-service";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { IWorkspaceRole } from "./role-aggregation";

interface IRenameRoleModalProps {
  role: IWorkspaceRole | null;
  showModal: boolean;
  closeModal: () => void;
  onRenamed: () => void;
}

// KOA-18634: rename an app role across every project that uses it. The role keeps its id in each
// project, so the change is a pure name update that the running apps apply in place on deploy. The
// affected projects are listed so the user sees this is workspace-wide, not project-local.
const RenameRoleModal = (props: IRenameRoleModalProps): JSX.Element | null => {
  const { role, showModal, closeModal, onRenamed } = props;
  const [newName, setNewName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect((): void => {
    if (showModal && role !== null) {
      setNewName(role.name);
    }
  }, [showModal, role]);

  const handleRename = async (): Promise<void> => {
    if (role === null) {
      return;
    }
    const trimmed: string = newName.trim();
    if (trimmed.length === 0 || trimmed === role.name) {
      return;
    }
    setIsSubmitting(true);
    try {
      const result: AxiosResponse = await DomainService.RenameWorkspaceRole(role.name, trimmed);
      if (result && result.status === 200) {
        KMainFunctions.displaySuccessNotification(`Role renamed to "${trimmed}".`);
        onRenamed();
        closeModal();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (role === null) {
    return null;
  }

  const projectNames: string[] = role.occurrences
    .map((occurrence: IDomainWideProjectRole): string => occurrence.projectName)
    .filter((name: string, index: number, all: string[]): boolean => all.indexOf(name) === index);

  const trimmedNewName: string = newName.trim();
  const canSubmit: boolean = trimmedNewName.length > 0 && trimmedNewName !== role.name && !isSubmitting;

  return (
    <KModal
      showModal={showModal}
      closeModal={closeModal}
      header={`Rename "${role.name}"`}
      width={480}
      footer={
        <KModalFooter
          submitLabel="Rename"
          onSubmit={handleRename}
          onCancel={closeModal}
          loading={isSubmitting}
          disabled={!canSubmit}
        />
      }
    >
      <div className="kuika-rename-role">
        <KuikaInput
          data-testid="renameRoleInput"
          value={newName}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => setNewName(event.currentTarget.value)}
          placeholder="Role name"
        />
        <KuikaSmallText>This renames the role in every project that uses it:</KuikaSmallText>
        <div className="kuika-rename-role__projects">
          {projectNames.map((name: string): React.ReactNode => (
            <KuikaBadge key={name} type="label" color="grey" variant="subtle" size="small" content={name} />
          ))}
        </div>
      </div>
    </KModal>
  );
};

export { RenameRoleModal };
